import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter, createRoute, createRootRoute } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster, type ToasterProps } from "sonner";
import { useTheme } from "@/features/theme";
import { configureAuth } from "@/features/auth/auth-config";
import { AuthProvider } from "@/features/auth";
import { ThemeProvider } from "@/features/theme";
import { AppLayout } from "@/components/layout/AppLayout";
import { LoginPage } from "@/routes/LoginPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { CreationHubPage } from "@/routes/CreationHubPage";
import { LinksPage } from "@/routes/LinksPage";
import { LinkDetailPage } from "@/routes/LinkDetailPage";
import { AnalyticsPage } from "@/routes/AnalyticsPage";
import { ApiKeysPage } from "@/routes/ApiKeysPage";
import { TestConsolePage } from "@/routes/TestConsolePage";
import "@/globals.css";

function ThemedToaster(props: ToasterProps) {
  const { resolvedTheme } = useTheme();
  return <Toaster theme={resolvedTheme} {...props} />;
}

// Configure Amplify Auth
configureAuth();

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Router Definition
const rootRoute = createRootRoute();

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "layout",
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/",
  component: DashboardPage,
});

const createRoute_ = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/create",
  component: CreationHubPage,
});

const linksRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/links",
  component: LinksPage,
});

const linkDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/links/$shortCode",
  component: LinkDetailPage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/api-keys",
  component: ApiKeysPage,
});

const testConsoleRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/test-console",
  component: TestConsolePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    createRoute_,
    linksRoute,
    linkDetailRoute,
    analyticsRoute,
    apiKeysRoute,
    testConsoleRoute,
  ]),
]);

const router = createRouter({
  routeTree,
  basepath: "/",
});

// Render
const rootElement = document.getElementById("root")!;
createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <ThemedToaster position="top-right" richColors />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
